/**
 * Base error class for all custom errors in the application
 */
export class AppError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly isOperational: boolean;
  public readonly metadata?: Record<string, any>;

  constructor(
    message: string,
    code: string,
    status: number = 500,
    isOperational: boolean = true,
    metadata?: Record<string, any>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.status = status;
    this.isOperational = isOperational;
    this.metadata = metadata;

    Error.captureStackTrace(this, this.constructor);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      ...(this.metadata && { metadata: this.metadata }),
    };
  }
}

/**
 * Authentication errors (401)
 */
export class AuthError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'AUTH_ERROR', 401, true, metadata);
  }
}

/**
 * Authorization errors (403)
 */
export class ForbiddenError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'FORBIDDEN_ERROR', 403, true, metadata);
  }
}

/**
 * Payment/Credit errors (402)
 */
export class PaymentError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'PAYMENT_ERROR', 402, true, metadata);
  }
}

/**
 * Rate limit errors (429)
 */
export class RateLimitError extends AppError {
  constructor(message: string, retryAfter?: number, metadata?: Record<string, any>) {
    super(
      message,
      'RATE_LIMIT_ERROR',
      429,
      true,
      { retryAfter, ...metadata }
    );
  }
}

/**
 * Validation errors (400)
 */
export class ValidationError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'VALIDATION_ERROR', 400, true, metadata);
  }
}

/**
 * Not found errors (404)
 */
export class NotFoundError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'NOT_FOUND_ERROR', 404, true, metadata);
  }
}

/**
 * API/Network errors (502, 503, 504)
 */
export class ApiError extends AppError {
  constructor(
    message: string,
    status: number = 502,
    metadata?: Record<string, any>
  ) {
    super(message, 'API_ERROR', status, true, metadata);
  }
}

/**
 * Image processing errors
 */
export class ImageProcessingError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'IMAGE_PROCESSING_ERROR', 500, true, metadata);
  }
}

/**
 * Storage errors
 */
export class StorageError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'STORAGE_ERROR', 500, true, metadata);
  }
}

/**
 * Configuration errors (500) - Not operational, server misconfiguration
 */
export class ConfigurationError extends AppError {
  constructor(message: string, metadata?: Record<string, any>) {
    super(message, 'CONFIGURATION_ERROR', 500, false, metadata);
  }
}

/**
 * Unknown/Unexpected errors (500) - Not operational
 */
export class UnknownError extends AppError {
  constructor(message: string = 'An unexpected error occurred', metadata?: Record<string, any>) {
    super(message, 'UNKNOWN_ERROR', 500, false, metadata);
  }
}
